import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getApiBaseUrl } from '@/lib/api'
import { supabase } from '@/integrations/supabase/client'
import { useTeamContext } from '@/context/TeamContext'
import type { CanonicalPlanId } from '@/lib/entitlements'

export interface LimitStatus {
	allowed: boolean
	limit: number | null // null if limit not defined, -1 for unlimited
	currentUsage?: number
	message?: string
	planId?: CanonicalPlanId
	unlimitedProjects?: boolean
}

export type FeatureKey =
	| 'max_programs'
	| 'max_active_widgets'
	| 'can_remove_branding'
	| 'can_use_bug_reporting'
	| 'can_use_feature_requests'
	| 'can_use_live_chat'
	| 'max_webhooks'

export function useFeatureLimit(
	featureKey: FeatureKey,
	options?: {
		enabled?: boolean
		widgetIdToExclude?: string // For widget-related checks
		programId?: string // For program-specific checks (needed for invited users)
	}
) {
	const { teamId } = useTeamContext()
	const { i18n } = useTranslation()
	const lang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
	const { enabled = true, widgetIdToExclude, programId } = options || {}

	const query = useQuery<LimitStatus, Error>({
		queryKey: ['featureLimit', teamId, featureKey, widgetIdToExclude, programId, lang],
		queryFn: async (): Promise<LimitStatus> => {
			// Note: teamId is now handled by the backend via auth token, no need to check here

			try {
				// Get auth headers
				const {
					data: { session },
				} = await supabase.auth.getSession()
				const headers = {
					Authorization: `Bearer ${session?.access_token}`,
					'Content-Type': 'application/json',
					'X-Language': lang,
				}

				// Build query params
				const queryParams = new URLSearchParams()
				if (widgetIdToExclude) {
					queryParams.append('widgetIdToExclude', widgetIdToExclude)
				}
				if (programId) {
					queryParams.append('programId', programId)
				}

				const url = `${getApiBaseUrl()}/api/limits/feature/${featureKey}${
					queryParams.toString() ? '?' + queryParams.toString() : ''
				}`

				const response = await fetch(url, { headers })

				if (!response.ok) {
					const errorData = await response
						.json()
						.catch(() => ({ error: 'Unknown error' }))
					throw new Error(errorData.error || `HTTP ${response.status}`)
				}

				const data = await response.json()

				const normalizedLimit =
					typeof data.limit === 'string' ? Number(data.limit) : data.limit
				const limit =
					normalizedLimit === null || Number.isNaN(normalizedLimit)
						? data.limit
						: normalizedLimit
				const isUnlimited = limit === -1

				return {
					...(data as LimitStatus),
					limit,
					allowed: isUnlimited ? true : Boolean(data.allowed),
				}
			} catch (err) {
				console.error(`Error checking limit for ${featureKey}:`, err)
				return {
					allowed: false,
					limit: 0,
					message: 'Error checking feature limit.',
				}
			}
		},
		enabled: enabled && !!featureKey,
		staleTime: 1 * 60 * 1000, // Cache for 1 minute
		gcTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false, // Don't refetch on focus by default
	})

	// Return the individual properties instead of the query object
	return {
		data: query.data,
		allowed: query.data?.allowed ?? false,
		limit: query.data?.limit ?? null,
		currentUsage: query.data?.currentUsage ?? 0,
		message: query.data?.message ?? undefined,
		planId: query.data?.planId ?? undefined,
		isLoading: query.isLoading,
		refetch: query.refetch,
		error: query.error,
	}
}
